import {
  adminStreamerSchema,
  openChecklistsSchema,
  type AdminStreamer,
  type ApplicationSummary,
  type OpenChecklist,
} from '@growup/streaming-shared';
import { Card, Notice, cx } from '@growup/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { z } from 'zod';

import { getJson, messageOf } from '../api';
import { formatDate } from '../format';

const DAY_MS = 86_400_000;

/** Dias ate `iso` (negativo = ja passou). */
const daysUntil = (iso: string) => Math.ceil((new Date(iso).getTime() - Date.now()) / DAY_MS);

/**
 * Resumo da seccao Streaming (arrumar a gestao): o que precisa do Stream Staff
 * hoje. Quatro contadores que levam a vista certa e as checklists dos primeiros
 * 30 dias em curso, as que terminam primeiro a cabeca.
 */
export function OverviewView({
  summary,
  attention,
  onGo,
  onOpenStreamer,
}: {
  summary: ApplicationSummary | null;
  attention: number;
  onGo: (tab: 'candidaturas' | 'padrinhos' | 'atencao' | 'streamers') => void;
  onOpenStreamer: (accountId: string) => void;
}) {
  const [checklists, setChecklists] = useState<OpenChecklist[] | null>(null);
  const [streamers, setStreamers] = useState<AdminStreamer[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getJson('/api/streaming/checklists', openChecklistsSchema)
      .then(setChecklists)
      .catch((e: unknown) => setError(messageOf(e)));
    getJson('/api/streaming/streamers', z.array(adminStreamerSchema))
      .then(setStreamers)
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);

  const withoutPadrinho =
    streamers?.filter((s) => s.status !== 'removed' && s.padrinho === null).length ?? null;
  const requests = streamers?.filter((s) => s.padrinhoRequest !== null).length ?? 0;
  const endingSoon = checklists?.filter((c) => daysUntil(c.dueOn) <= 7).length ?? 0;

  return (
    <div className="flex flex-col gap-4">
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Counter
          title="Candidaturas"
          value={summary?.pending ?? 0}
          note={
            summary && summary.overdue > 0 ? (
              <span className="text-gu-danger">{summary.overdue} fora do prazo</span>
            ) : (
              'por responder'
            )
          }
          onClick={() => onGo('candidaturas')}
        />
        <Counter
          title="Sem padrinho"
          value={withoutPadrinho}
          note={
            requests > 0
              ? `${String(requests)} ${requests === 1 ? 'pede' : 'pedem'} outro padrinho`
              : 'atribuir em Padrinhos'
          }
          onClick={() => onGo('padrinhos')}
        />
        <Counter
          title="Precisa de atenção"
          value={attention}
          note="pelos relatórios dos padrinhos"
          onClick={() => onGo('atencao')}
        />
        <Counter
          title="Primeiros 30 dias"
          value={checklists?.length ?? null}
          note={
            endingSoon > 0
              ? `${String(endingSoon)} ${endingSoon === 1 ? 'termina' : 'terminam'} esta semana`
              : 'checklists abertas'
          }
          onClick={() => onGo('streamers')}
        />
      </div>
      <Card headingLevel={2} title="Primeiros 30 dias em curso">
        {checklists === null ? <p className="text-gu-ink-muted">A carregar…</p> : null}
        {checklists && checklists.length === 0 ? (
          <p className="text-gu-ink-muted">Ninguém está nos primeiros 30 dias.</p>
        ) : null}
        {checklists && checklists.length > 0 ? (
          <ul className="flex flex-col divide-y divide-gu-line">
            {checklists.map((c) => {
              const left = daysUntil(c.dueOn);
              const pct = c.total > 0 ? Math.round((c.done / c.total) * 100) : 0;
              return (
                <li key={c.streamerId}>
                  <button
                    type="button"
                    className="grid w-full grid-cols-2 items-center gap-x-4 gap-y-1 py-3 text-left hover:bg-gu-sunken sm:grid-cols-[1fr_8rem_1fr_7rem]"
                    onClick={() => onOpenStreamer(c.accountId)}
                  >
                    <span className="font-semibold">{c.displayName}</span>
                    <span className="text-gu-ink-muted">
                      {c.padrinho ?? <span className="text-gu-warning">sem padrinho</span>}
                    </span>
                    <span className="flex items-center gap-2">
                      <span
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={c.total}
                        aria-valuenow={c.done}
                        aria-label={`${c.displayName}: ${String(c.done)} de ${String(c.total)}`}
                        className="h-2 flex-1 overflow-hidden rounded-full bg-gu-sunken"
                      >
                        <span
                          className="block h-full bg-gu-accent"
                          style={{ width: `${String(pct)}%` }}
                        />
                      </span>
                      <span className="text-caption tabular-nums">
                        {c.done}/{c.total}
                      </span>
                    </span>
                    <span
                      className={cx(
                        'text-caption',
                        left < 0
                          ? 'text-gu-danger'
                          : left <= 7
                            ? 'text-gu-warning'
                            : 'text-gu-ink-muted',
                      )}
                    >
                      {formatDate(c.dueOn)}
                      {left < 0 ? ' · passou' : left <= 7 ? ' · esta semana' : ''}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </Card>
    </div>
  );
}

function Counter({
  title,
  value,
  note,
  onClick,
}: {
  title: string;
  value: number | null;
  note: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col gap-1 border border-gu-line bg-gu-raised p-4 text-left hover:border-gu-accent"
    >
      <span className="font-sub text-label font-bold uppercase text-gu-ink-muted">{title}</span>
      <span className="font-display text-headline">{value ?? '…'}</span>
      <span className="text-caption text-gu-ink-muted">{note}</span>
    </button>
  );
}
